import { alpha, Button, ButtonGroup, Theme, useTheme } from '@mui/material';
import type { SxProps } from '@mui/system';
import { ConversationDraftSendOptionSection } from '@provision/conversation-types';
import { type MouseEvent, MutableRefObject, type ReactElement, useCallback } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { SendButton } from 'src/components/RichTextEditor/buttons';

export interface SendOptionItem {
  id: string;
  label: string;
  section: ConversationDraftSendOptionSection;
  description?: string;
  icon?: string;
}

interface SendOptionsButtonProps {
  buttonText?: string;
  disabled?: boolean;
  isLoading?: boolean;
  onSend: () => void;
  buttonRef: MutableRefObject<HTMLButtonElement | null>;
  startIcon?: string;
  onOpenOptions: () => void;
  /** Note mode swaps the caret hairline tint. */
  isInternalNote?: boolean;
}

const buttonGroupSx: SxProps<Theme> = {
  flexShrink: 0,
  boxShadow: 'none',
  borderRadius: '0.5rem',
  overflow: 'hidden',
  '& .MuiButton-root:hover': { filter: 'brightness(1.07)', boxShadow: 'none' },
  '& .MuiButton-root.Mui-disabled': { boxShadow: 'none' },
  // MUI's own borderRight here would double the delimiter against the caret's borderLeft
  // (including when the send action disables and reasserts it).
  '& .MuiButtonGroup-firstButton, & .MuiButtonGroup-middleButton': {
    borderRight: 'none',
    '&.Mui-disabled': { borderRight: 'none' },
  },
};

export const SendOptionsButton = ({
  buttonText = 'Send',
  disabled,
  isLoading,
  onSend,
  buttonRef,
  startIcon,
  onOpenOptions,
  isInternalNote,
}: SendOptionsButtonProps): ReactElement => {
  const theme = useTheme();
  const handleToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      onOpenOptions();
    },
    [onOpenOptions],
  );

  const dividerColor = isInternalNote
    ? alpha(theme.palette.custom.OV.extended.Signature.color, 0.32)
    : alpha(theme.palette.common.white, 0.28);

  // Disabled reads as a muted version of the active fill, not MUI's flat grey slab — so it
  // derives from whichever fill/ink pair the current mode and theme actually use.
  const isLightNote = isInternalNote && theme.palette.mode === 'light';
  const noteFillColor = isLightNote
    ? theme.palette.custom.OV.extended.Signature.colorContainer
    : theme.palette.custom.OV.custom.InternalNotes.noteButton;
  const disabledFillColor = isInternalNote ? noteFillColor : theme.palette.primary.main;
  const disabledInkColor = isLightNote
    ? theme.palette.custom.OV.extended.Signature.color
    : theme.palette.common.white;
  const disabledStateSx = {
    backgroundColor: alpha(disabledFillColor, 0.35),
    color: alpha(disabledInkColor, 0.6),
    boxShadow: 'none',
  };
  const disabledSx: SxProps<Theme> = { '&.Mui-disabled': disabledStateSx };

  const caretButtonSx: SxProps<Theme> = {
    minWidth: 0,
    minHeight: '1.875rem',
    [theme.breakpoints.down('md')]: { minHeight: '2.25rem', padding: theme.spacing(0, 1.25) },
    padding: theme.spacing(0, 0.875),
    borderLeft: `1px solid ${dividerColor}`,
    ...disabledSx,
    '&.Mui-disabled': { ...disabledStateSx, borderLeft: `1px solid ${dividerColor}` },
  };

  return (
    <ButtonGroup variant="contained" disableElevation sx={buttonGroupSx}>
      <SendButton
        ref={buttonRef}
        type="submit"
        buttonText={buttonText}
        isLoading={isLoading}
        onClick={onSend}
        disabled={disabled || isLoading}
        startIcon={startIcon}
        size="small"
        sx={disabledSx}
      />
      <Button
        type="button"
        variant="contained"
        color="primary"
        disabled={disabled || isLoading}
        onClick={handleToggle}
        sx={caretButtonSx}
        aria-haspopup="listbox"
        aria-label="Open Smart Send"
        data-testid="send-options-button"
        aria-expanded="false"
        aria-controls="send-options-autocomplete"
      >
        <DynamicIcon library="lucide" name="chevron-down" size={15} />
      </Button>
    </ButtonGroup>
  );
};

export default SendOptionsButton;
